import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Message, Conversation } from '@/types/chat';
import { isNoteMessage } from '@/utils/message-helpers';

interface UseChatOptions {
  bookingId?: string;
  groupEventId?: string;
  userType?: 'client' | 'practitioner';
}

export function useChat(bookingIdOrOptions: string | UseChatOptions, userType?: 'client' | 'practitioner') {
  // Support both legacy (bookingId, userType) and new ({ bookingId, groupEventId, userType }) signatures
  const options: UseChatOptions = typeof bookingIdOrOptions === 'string'
    ? { bookingId: bookingIdOrOptions, userType }
    : bookingIdOrOptions;

  const bookingId = options.bookingId;
  const groupEventId = options.groupEventId;
  const resolvedUserType = options.userType;

  const { user, activeRole } = useAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);

  const senderType = resolvedUserType || activeRole || 'client';
  const hasIdentifier = !!(bookingId || groupEventId);

  // Get or create conversation — supports both booking and group event
  const getOrCreateConversation = useCallback(async () => {
    if (!user || !hasIdentifier) return;

    try {
      // Build query based on identifier type
      let query = supabase.from('conversations').select('*');
      if (bookingId) {
        query = query.eq('booking_id', bookingId);
      } else if (groupEventId) {
        query = query.eq('group_event_id', groupEventId);
      }

      let existingConversation = null;
      let retries = 0;
      const maxRetries = 3;

      while (retries < maxRetries && !existingConversation) {
        const result = await query.maybeSingle();
        existingConversation = result.data;

        if (existingConversation) {
          console.log('✅ Found existing conversation:', existingConversation.id);
          setConversation(existingConversation);
          return existingConversation;
        }

        if (result.error && result.error.code !== 'PGRST116') {
          console.error('❌ Error fetching conversation:', result.error);
          toast({ title: "Error", description: "Failed to load conversation", variant: "destructive" });
          return null;
        }

        retries++;
        if (retries < maxRetries) await new Promise(r => setTimeout(r, 200));
      }

      // Create new conversation
      const insertPayload: any = {};
      if (bookingId) {
        insertPayload.booking_id = bookingId;
      } else if (groupEventId) {
        insertPayload.group_event_id = groupEventId;
      }

      const upsertOptions: any = {};
      if (bookingId) upsertOptions.onConflict = 'booking_id';
      else if (groupEventId) upsertOptions.onConflict = 'group_event_id';

      const { data: newConversation, error: createError } = await supabase
        .from('conversations')
        .upsert(insertPayload, upsertOptions)
        .select()
        .single();

      if (createError) {
        console.error('❌ Error creating conversation:', createError);
        // If duplicate, try fetching again
        if (createError.code === '23505' || createError.message?.includes('duplicate')) {
          let retryQuery = supabase.from('conversations').select('*');
          if (bookingId) retryQuery = retryQuery.eq('booking_id', bookingId);
          else if (groupEventId) retryQuery = retryQuery.eq('group_event_id', groupEventId);
          
          const { data: existing } = await retryQuery.maybeSingle();
          if (existing) {
            setConversation(existing);
            return existing;
          }
        }
        toast({ title: "Error", description: "Failed to create conversation", variant: "destructive" });
        return null;
      }

      console.log('✅ Conversation created:', newConversation?.id);
      setConversation(newConversation);
      return newConversation;
    } catch (error) {
      console.error('❌ Unexpected error in getOrCreateConversation:', error);
      return null;
    }
  }, [user, bookingId, groupEventId, hasIdentifier, toast]);

  // Load messages
  const loadMessages = useCallback(async (conversationId: string) => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) {
        toast({ title: "Error", description: "Failed to load messages", variant: "destructive" });
        return;
      }

      const chatOnly = (data || []).filter(msg => !isNoteMessage(msg.message_content));
      const typedMessages = chatOnly.map(msg => ({
        ...msg,
        sender_type: msg.sender_type as 'client' | 'practitioner'
      })) as Message[];

      setMessages(typedMessages);
    } catch (error) {
      console.error('Unexpected error loading messages:', error);
    }
  }, [toast]);

  // Send message
  const sendMessage = useCallback(async (content: string) => {
    if (!user || !conversation || !content.trim()) return;

    setIsSending(true);
    try {
      const { error } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_id: user.id,
          sender_type: senderType,
          message_content: content.trim(),
          message_type: 'text',
          is_read: false,
        });

      if (error) {
        toast({ title: "Error", description: "Failed to send message", variant: "destructive" });
        return;
      }

      // Send notification via edge function (handles email + SMS for all scenarios)
      try {
        await supabase.functions.invoke('send-chat-notification', {
          body: {
            conversationId: conversation.id,
            senderId: user.id,
            senderType,
            messageContent: content.trim(),
          }
        });
      } catch (notifyError) {
        console.error('Error sending chat notification:', notifyError);
      }
    } catch (error) {
      toast({ title: "Error", description: "An unexpected error occurred", variant: "destructive" });
    } finally {
      setIsSending(false);
    }
  }, [user, conversation, toast, bookingId, groupEventId, senderType]);

  // Mark messages as read
  const markMessagesAsRead = useCallback(async () => {
    if (!user || !conversation) return;
    try {
      await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('conversation_id', conversation.id)
        .neq('sender_id', user.id)
        .eq('is_read', false);

      setMessages(prev => prev.map(msg => msg.sender_id !== user.id ? { ...msg, is_read: true } : msg));
    } catch (error) {
      console.error('Unexpected error marking messages as read:', error);
    }
  }, [user, conversation]);

  // Initialize
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      const conv = await getOrCreateConversation();
      if (conv) await loadMessages(conv.id);
      setIsLoading(false);
    };

    if (user && hasIdentifier) {
      initialize();
    }
  }, [user, hasIdentifier, getOrCreateConversation, loadMessages]);

  // Realtime subscription
  useEffect(() => {
    if (!conversation) return;

    const channel = supabase
      .channel(`messages-${conversation.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversation.id}`,
      }, (payload) => {
        if (payload.eventType === 'INSERT') {
          if (isNoteMessage(payload.new.message_content)) return;
          const newMessage = { ...payload.new, sender_type: payload.new.sender_type as 'client' | 'practitioner' } as Message;
          setMessages(prev => [...prev, newMessage]);
        } else if (payload.eventType === 'UPDATE') {
          setMessages(prev => prev.map(msg => msg.id === payload.new.id ? { ...msg, ...payload.new } : msg));
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [conversation]);

  return { messages, conversation, isLoading, isSending, sendMessage, markMessagesAsRead };
}
